import { Icon } from "@shopify/polaris"
import { MobileVerticalDotsMajor } from "@shopify/polaris-icons"
import React, { useEffect, useRef, useState } from "react"
import ReactDOM from "react-dom"
import { useTranslation } from "react-i18next"
import { useAppContext } from "components/AppContext"
import useRootNode from "hooks/useRootNode"
import UserStateEntity from "services/entities/UserStateEntity"
import OrganizationEntity from "services/entities/OrganizationEntity"
import { useEntity } from "hooks/useAxios"
import "stylesheets/navigation/ProductNavigation.scss"
import { TOAST_STATUS_ERROR } from "utils/constants/toastStatuses"
import CorporateIt from "assets/images/logos/CorporateIT"
import ITProvider from "assets/images/logos/ITProvider"
import ProductSection from "./ProductSection"

const ProductNavigation = () => {
  const { t } = useTranslation()
  const userStateEntity = useEntity(UserStateEntity)
  const organizationEntity = useEntity(OrganizationEntity)
  const {
    productDetails,
    currentRootNode,
    currentUser,
    currentOrganization,
    reloadCurrentRoot,
    reloadCurrentOrganization,
    reloadCurrentUser,
    setToastStatus,
  } = useAppContext()
  const { changeRoot } = useRootNode()

  const [active, setActive] = useState(false)
  const [navData, setNavData] = useState(null)
  const [corporateExpanded, setCorporateExpanded] = useState(false)
  const [providerExpanded, setProviderExpanded] = useState(false)
  const [panelStyle, setPanelStyle] = useState({})
  const panelRef = useRef(null)
  const buttonRef = useRef(null)

  const toggle = () => {
    if (!active && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setPanelStyle({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      })
    }
    setActive((prev) => !prev)
  }

  // Close on outside click or Escape
  useEffect(() => {
    if (!active) return
    const handleClick = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setActive(false)
      }
    }
    const handleKey = (e) => {
      if (e.key === "Escape") setActive(false)
    }
    document.addEventListener("mousedown", handleClick)
    document.addEventListener("keydown", handleKey)
    return () => {
      document.removeEventListener("mousedown", handleClick)
      document.removeEventListener("keydown", handleKey)
    }
  }, [active])

  // Fetch data on open
  useEffect(() => {
    if (!active) return
    const fetchNavData = async () => {
      try {
        const data = await organizationEntity.getNavigationOrganizations()
        setNavData(data)
        setCorporateExpanded(!!currentRootNode?.is_corporate)
        setProviderExpanded(!!currentRootNode?.is_provider)
      } catch {
        setNavData(null)
      }
    }
    fetchNavData()
  }, [active])

  const handleSelectOrg = async (org, fromProduct) => {
    setActive(false)
    const currentProduct = currentRootNode?.is_corporate
      ? "corporate"
      : currentRootNode?.is_provider
        ? "provider"
        : null
    const isSameProduct = fromProduct === currentProduct

    const targetRootId =
      org.root_node_id ||
      (fromProduct === "corporate"
        ? navData?.corporate?.root_id
        : navData?.provider?.root_id)

    if (!targetRootId) return

    if (
      isSameProduct &&
      org.id === currentOrganization?.id &&
      targetRootId === currentRootNode?.id
    ) {
      return
    }

    try {
      // setCurrentRoot makes the user a member of the root's organization
      // (home + current organization follow), so no separate setOrganization
      // call is needed here.
      const rootResponse = await userStateEntity.setCurrentRoot(targetRootId)
      await changeRoot(rootResponse, "/")
      reloadCurrentOrganization && reloadCurrentOrganization()
      reloadCurrentUser && reloadCurrentUser()
    } catch (error) {
      console.error("Failed to switch organization", error)
      setToastStatus(TOAST_STATUS_ERROR)
    }
  }

  if (!productDetails || !currentRootNode || !currentUser) return null

  const corporateDisabled = !(
    currentUser.can_switch_product || currentRootNode?.is_corporate
  )
  const providerDisabled = !(
    currentUser.can_switch_product || currentRootNode?.is_provider
  )

  const hasCorporate = navData?.corporate?.organizations?.length > 0
  const hasProvider = navData?.provider?.organizations?.length > 0

  const sections = []
  if (currentRootNode?.is_corporate) {
    if (hasCorporate)
      sections.push({ product: "corporate", logo: CorporateIt, organizations: navData.corporate.organizations, expanded: corporateExpanded, onToggle: () => setCorporateExpanded((p) => !p), disabled: false })
    if (hasProvider)
      sections.push({ product: "provider", logo: ITProvider, organizations: navData.provider.organizations, expanded: providerExpanded, onToggle: () => setProviderExpanded((p) => !p), disabled: providerDisabled })
  } else {
    if (hasProvider)
      sections.push({ product: "provider", logo: ITProvider, organizations: navData.provider.organizations, expanded: providerExpanded, onToggle: () => setProviderExpanded((p) => !p), disabled: false })
    if (hasCorporate)
      sections.push({ product: "corporate", logo: CorporateIt, organizations: navData.corporate.organizations, expanded: corporateExpanded, onToggle: () => setCorporateExpanded((p) => !p), disabled: corporateDisabled })
  }

  const panel = active && ReactDOM.createPortal(
    <div
      className="pnav__panel"
      ref={panelRef}
      style={{ top: panelStyle.top, right: panelStyle.right }}
    >
      {!navData ? (
        <div className="pnav__loading">{t("common:navigation.loading")}</div>
      ) : sections.length === 0 ? (
        <div className="pnav__empty">{t("common:navigation.empty")}</div>
      ) : (
        sections.map((section) => (
          <ProductSection
            key={section.product}
            logo={section.logo}
            organizations={section.organizations}
            expanded={section.expanded}
            onToggle={section.onToggle}
            onSelectOrg={handleSelectOrg}
            currentOrgId={currentOrganization?.id}
            disabled={section.disabled}
            product={section.product}
          />
        ))
      )}
    </div>,
    document.body
  )

  return (
    <>
      <button ref={buttonRef} onClick={toggle} className="pnav__trigger">
        <Icon source={MobileVerticalDotsMajor} tone="base" />
      </button>
      {panel}
    </>
  )
}

export default ProductNavigation
